/* eslint-disable react/display-name */
import React, { useState } from 'react';

import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Check, HookFormResolver } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { message } from 'app/modules/Taxpayer/TaxpayerRequest/translation';
import { Alert, ButtonGroup, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { type EconomyActivity } from '../../../Operations/EconomyActivity/types';
import { useOperationsList } from '../../../Operations/EconomyActivity/useOperation';
import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import ProgressBar from '../components/progress-bar';
import { ValidationsResolver } from '../rules';
import { rulesContactData } from '../rules/contact';

import { type OperationsModel, type ProgressStepProps } from './types';
interface Props {
  onSubmit: (data: any) => void;
  operations: Array<EconomyActivity>;
  readonly?: boolean;
}

export const Operation = ({ onSubmit, operations, readonly }: Props) => {
  const { t, i18n } = useTranslation();
  const [{ data, loading, error }] = useOperationsList();
  const taxpayer = operations;
  const filterSelect = (result: OperationsModel) => {
    const list = (data || []).filter(
      (c, index) => (result.selectedOperations as any)[index] === true
    );

    onSubmit(list);
  };
  const active = (obj?: any): boolean => {
    if (taxpayer) {
      const result = taxpayer.filter(
        (economyActivity: EconomyActivity) => (economyActivity as any).id == obj.id
      );
      return result.length > 0;
    }
    return false;
  };

  if (loading || error) {
    return <></>;
  }

  if (data?.length === 0 || data === undefined || data === null) {
    return (
      <>
        <Row>
          <Col>
            <Alert variant="warning">{message(t, 'withoutOperations')}</Alert>
          </Col>
        </Row>
      </>
    );
  }

  return (
    <>
      <ValidationsResolver rules={rulesContactData}>
        {({ validations }: any) => {
          return (
            <HookFormResolver nameForm={'formOperations'} onSubmit={filterSelect}>
              {({ formState: { errors }, reset, setValue, watch }: HookFormMethods) => {
                return (
                  <>
                    <hr />
                    <h2>{t('taxpayer-request.messages.selectOperations')}</h2>

                    {data && data.length > 0 && (
                      <Row>
                        <Col>
                          {data.map((obj: any, index) => {
                            const fieldName = `selectedOperations[${index}]`;
                            const selected = Boolean(watch(fieldName) ?? active(obj));
                            const toggleSelected = () => {
                              if (!readonly) {
                                setValue(fieldName, !selected);
                              }
                            };

                            return (
                              <React.Fragment key={obj.id}>
                                <div
                                  className={`card mb-3 ${selected ? 'border-primary' : ''}`}
                                  role={readonly ? undefined : 'button'}
                                  tabIndex={readonly ? undefined : 0}
                                  onClick={toggleSelected}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      toggleSelected();
                                    }
                                  }}
                                >
                                  <div className="card-body">
                                    <h5 className="card-title">{obj.name}</h5>

                                    <div onClick={(event) => event.stopPropagation()}>
                                      <Check
                                        label={obj.description}
                                        name={fieldName}
                                        defaultValue={selected}
                                        disabled={readonly}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </Col>
                      </Row>
                    )}
                  </>
                );
              }}
            </HookFormResolver>
          );
        }}
      </ValidationsResolver>
    </>
  );
};

export default (props: ProgressStepProps) => {
  const [dirty, setDirty] = useState(false);
  const taxpayer = useTaxpayerRequestStore((s) => s.form.operations);
  const addOperationsData = useTaxpayerRequestStore((s) => s.addOperationsData);
  const { t, i18n } = useTranslation();
  const onSubmit = (data: Array<EconomyActivity>) => {
    data?.length == 0 ? setDirty(true) : props.nextStep();
    addOperationsData(data);
  };

  return (
    <>
      <Operation operations={taxpayer ? taxpayer : []} onSubmit={onSubmit} />
      {dirty && taxpayer?.length == 0 && (
        <div className="invalid-feedback d-block">{t('taxpayer-request.messages.operations')}</div>
      )}
      <ProgressBar percentage={props.percentage} />
      <BtnToolbar>
        <ButtonGroup>
          <Button
            form="formTextData"
            action={ButtonAction.previous}
            onClick={() => props.previousStep()}
          />
        </ButtonGroup>
        <ButtonGroup>
          <Button
            form="formOperations"
            variant="primary"
            action={ButtonAction.next}
            type={'submit'}
          />
        </ButtonGroup>
      </BtnToolbar>
    </>
  );
};
